import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TransferQuestionsModule } from './shared/components/transfer-questions/transfer-questions.module';
import { MainTransferQuestionModule } from './shared/components/main-transfer-question/main-transfer-question.module';
import ptBr from '@angular/common/locales/pt';
import { StoreModule } from '@ngrx/store';
import { ExtraOptions, RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { BrowserModule } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  NgModule,
  LOCALE_ID,
  ErrorHandler,
  isDevMode,
  APP_INITIALIZER
} from '@angular/core';

import { TextUtils } from '@toro/common/shared/resources/text.utils';
import { FormsUtils } from '@toro/common/shared/resources/forms.utils';
import { HttpService } from '@toro/common/shared/services/http/http.service';
import { LayerService } from '@toro/common/shared/services/LayerService';
import { HubNavModule } from '@toro/common/shared/components/hub-nav/hub-nav.module';
import { BanksService } from '@toro/common/shared/services/banks/banks.service';
import { FooterModule } from '@toro/common/shared/components/footer/footer.module';
import { SvgIconModule } from '@toro/common/shared/components/svg-icon/svg-icon.module';
import { AppcuesModule } from '@toro/common/shared/services/appcues/appcues.module';
import { SentryService } from '@toro/common/shared/services/sentry/sentry.service';
import { ConfirmModule } from '@toro/common/shared/components/dialogs/confirm/confirm.module';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { BackendService } from '@toro/common/shared/services/backend/backend.service';
import { NetworkService } from '@toro/common/shared/services/network.services';
import { FinanceService } from '@toro/common/shared/services/finance.services';
import { ChatBlipModule } from '@toro/common/shared/components/chat-blip/chat-blip.module';
import { ZendeskHelpButtonModule } from '@toro/common/shared/components/zendesk-help-button/zendesk-help-button.module';
import { ComputerService } from '@toro/common/shared/services/computer/computer.service';
import { SettingsService } from '@toro/common/shared/services/settings/settings.service';
import { FeatureFlagsService } from './shared/services/feature-flags/feature-flags.service';
import { InternetService } from '@toro/common/shared/services/internet/internet.service';
import { TrackingService } from '@toro/common/shared/services/tracking/tracking.service';
import { RealtimeService } from '@toro/common/shared/services/realtime/realtime.service';
import { RealtimeQuoteService } from '@toro/common/shared/services/realtime/realtime-quote.service';
import { CpfCnpjPipeModule } from '@toro/common/shared/pipes/cpf-cnpj/cpf-cnpj.module';
import { TrAccordionModule } from '@toro/common/shared/components/tr-accordion/tr-accordion.module';
import { DateFormatService } from '@toro/common/shared/services/date-format/date-format.service';
import { LoginWraperService } from '@toro/common/shared/services/login.wraper.service';
import { UserManagerService } from '@toro/common/shared/services/users/user-manager.service';
import { UserProfileService } from '@toro/common/shared/services/users/user-profile.service';
import { CustomErrorHandler } from '@toro/common/shared/services/sentry/custom-error.service';
import { TrackingDirectiveModule } from '@toro/common/shared/directives/tracking/tracking.module';
import { PushNotificationService } from '@toro/common/shared/services/push-notification/push-notification.service';
import { AuthorizationWraperService } from '@toro/common/shared/services/authorization-wraper/authorization.wraper.service';
import { BackendToFrontendParserService } from '@toro/common/shared/services/parser/backend-to-frontend-parser.service';
import { UnauthorizedErrorHandlerModule } from '@toro/common/shared/services/unauthorized-error-handler/unauthorized-error-handler.module';
import { TrSpinnerButtonDirectiveModule } from '@toro/common/shared/directives';
import {
  TrSideMenuModule,
  SimpleModalModule,
  LiveSectionAdvisoryModule,
  ChatOnboardingModule
} from '@toro/common/shared/components';
import { RewardsModule } from '@toro/common/shared/components/dialogs/rewards/rewards.module';

/*  STORE  */
import { AuthGuard } from '@toro/common/shared/guards/auth.guard';
import { UsersReducer } from '@toro/common/shared/state-management/users/users.reducer';
import { UsersEffects } from '@toro/common/shared/state-management/users/users.effects';
import { FilesReducer } from '@toro/common/shared/state-management/files/files.reducer';
import { BanksReducer } from '@toro/common/shared/state-management/banks/banks.reducer';
import { BanksEffects } from '@toro/common/shared/state-management/banks/banks.effects';
import { UtilsReducer } from '@toro/common/shared/state-management/utils/utils.reducer';
import { UtilsEffects } from '@toro/common/shared/state-management/utils/utils.effects';
import { FilesEffects } from '@toro/common/shared/state-management/files/files.effects';
import { BillingReducer } from '@toro/common/shared/state-management/billing/billing.reducer';
import { FinanceEffects } from '@toro/common/shared/state-management/finance/finance.effects';
import { BillingEffects } from '@toro/common/shared/state-management/billing/billing.effects';
import { FinanceReducer } from '@toro/common/shared/state-management/finance/finance.reducer';
import { ExchangeReducer } from '@toro/common/shared/state-management/exchange/exchange.reducer';
import { ExchangeEffects } from '@toro/common/shared/state-management/exchange/exchange.effects';
import { ResearchReducer } from '@toro/common/shared/state-management/research/research.reducer';
import { ResearchEffects } from '@toro/common/shared/state-management/research/research.effects';
import { TrackingEffects } from '@toro/common/shared/state-management/tracking/tracking.effects';
import { AgreementsReducer } from '@toro/common/shared/state-management/agreements/agreements.reducer';
import { SalesForceEffects } from '@toro/common/shared/state-management/sales-force/sales-force.effects';
import { SalesForceReducer } from '@toro/common/shared/state-management/sales-force/sales-force.reducer';
import { AgreementsEffects } from '@toro/common/shared/state-management/agreements/agreements.effects';
import { LiveStreamReducer } from '@toro/common/shared/state-management/live-stream/live-stream.reducer';
import { LiveStreamEffects } from '@toro/common/shared/state-management/live-stream/live-stream.effects';
import { UserProfileReducer } from '@toro/common/shared/state-management/userprofile/userprofile.reducer';
import { UserProfileEffects } from '@toro/common/shared/state-management/userprofile/userprofile.effects';
import { InitializerEffects } from '@toro/common/shared/state-management/initializer/initializer.effects';
import { SuitabilityEffects } from '@toro/common/shared/state-management/suitability/suitability.effects';
import { SuitabilitySantanderEffects } from '@toro/common/shared/state-management/suitability-santander/suitability-santander.effects';
import { EnvironmentEffects } from '@toro/common/shared/state-management/environment/environment.effects';
import { EnvironmentReducer } from '@toro/common/shared/state-management/environment/environment.reducer';
import { SuitabilityReducer } from '@toro/common/shared/state-management/suitability/suitability.reducer';
import { FixedInvestReducer } from '@toro/common/shared/state-management/fixedInvest/fixedInvest.reducer';
import { FixedInvestEffects } from '@toro/common/shared/state-management/fixedInvest/fixedInvest.effects';
import { StocksMarketReducer } from '@toro/common/shared/state-management/stocks-market/stocks-market.reducer';
import { WealthEvolutionReducer } from '@toro/common/shared/state-management/wealth-evolution/wealth-evolution.reducer';
import { UserAcceptCampaignReducer } from '@toro/common/shared/state-management/user-accept-campaign/user-accept-campaign.reducer';
import { AdvisorReducer } from '@toro/common/shared/state-management/advisor/advisor.reducer';
import { StocksMarketEffects } from '@toro/common/shared/state-management/stocks-market/stocks-market.effects';
import { InfoproductsReducer } from '@toro/common/shared/state-management/infoproducts/infoproducts.reducer';
import { InfoproductsEffects } from '@toro/common/shared/state-management/infoproducts/infoproducts.effects';
import { NotificationsReducer } from '@toro/common/shared/state-management/notifications/notifications.reducer';
import { NotificationsEffects } from '@toro/common/shared/state-management/notifications/notifications.effects';
import { AuthorizationReducer } from '@toro/common/shared/state-management/authorization/authorization.reducer';
import { AuthorizationEffects } from '@toro/common/shared/state-management/authorization/authorization.effects';
import { RouterTrackerService } from '@toro/common/shared/services/router-tracker/router-tracker.service';
import { DigitalSubscriptionReducer } from '@toro/common/shared/state-management/digital-subscription/digital-subscription.reducer';
import { DigitalSubscriptionEffects } from '@toro/common/shared/state-management/digital-subscription/digital-subscription.effects';
import { StocksExchangeWalletReducer } from '@toro/common/shared/state-management/stocks-exchange-wallet/stocks-exchange-wallet.reducer';
import { StocksExchangeWalletEffects } from '@toro/common/shared/state-management/stocks-exchange-wallet/stocks-exchange-wallet.effects';
import { InstitutionalAccountsReducer } from '@toro/common/shared/state-management/institutional-accounts/institutional-accounts.reducer';
import { InstitutionalAccountsEffects } from '@toro/common/shared/state-management/institutional-accounts/institutional-accounts.effects';
import { StocksExchangeCheckoutReducer } from '@toro/common/shared/state-management/stocks-exchange-checkout/stocks-exchange-checkout.reducer';
import { StocksExchangeCheckoutEffects } from '@toro/common/shared/state-management/stocks-exchange-checkout/stocks-exchange-checkout.effects';
import { UserAcceptCampaignEffects } from '@toro/common/shared/state-management/user-accept-campaign/user-accept-campaign.effects';
import { AdvisorEffects } from '@toro/common/shared/state-management/advisor/advisor.effects';
import { AdvancedPlatformsReducer } from '@toro/common/shared/state-management/advanced-platforms/advanced-platforms.reducer';
import { AdvancedPlatformsEffects } from '@toro/common/shared/state-management/advanced-platforms/advanced-platforms.effects';
import { CustomerWalletEffects } from './state-management/customer-wallet/customer-wallet.effects';
import { OperationalLimitEffects } from '@toro/common/shared/state-management/operational-limit/operational-limit.effects';

import { routes } from './app.routes';
import { DraftModule } from './pages/draft/draft.module';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AgreementModule } from './pages/agreement/agreement.module';
import { HomeLoggedModule } from './pages/home-logged/home-logged.module';
import { TransferModule } from './pages/transfer/transfer.module';
import { CustomPlanComponent } from './pages/onboarding/custom-plan/custom-plan.component';
import { HeaderBalanceModule } from './shared/components/header-balance/header-balance.module';
import { TransferModalComponent } from './pages/onboarding/transfer-modal/transfer-modal.component';
import { CustomWalletBuilderModule } from './pages/onboarding/custom-wallet-builder/custom-wallet-builder.module';
import { CheckingAccountReceiptComponent } from './pages/home-logged/dialogs/checking-account-receipt/checking-account-receipt.component';
import { CustomerWalletReducer } from './state-management/customer-wallet/customer-wallet.reducer';
import { UserTagEffects } from './state-management/user-tag/user-tag.effects';
import { UserTagReducer } from './state-management/user-tag/user-tag.reducer';
import { TraderInfoEffects } from './state-management/trader/trader.effects';
import { TraderInfoReducer } from './state-management/trader/trader.reducer';
import { CustomerWalletRoutesModule } from './shared/modules/customer-wallet/customer-wallet.routes';
import { STVMDigitalReducer } from './state-management/stvm-digital/stvm-digital.reducer';
import { STVMDigitalEffects } from './state-management/stvm-digital/stvm-digital.effects';
import { OneViewPermissionsReducer } from './state-management/one-view-permissions/one-view-permissions.reducer';
import { OneViewPermissionsEffects } from './state-management/one-view-permissions/one-view-permissions.effects';
import { TrailInterceptor } from '@toro/common/shared/interceptors/trail.interceptor';
import { DialogDepositModule } from './shared/components/dialog-deposit/dialog-deposit.module';
import { DialogDepositComponent } from './shared/components/dialog-deposit/dialog-deposit.component';
import { FIREBASE_OPTIONS } from '@angular/fire';
import {
  AngularFireRemoteConfigModule,
  SETTINGS as REMOTE_CONFIG_SETTINGS,
  DEFAULTS as REMOTE_CONFIG_DEFAULTS
} from '@angular/fire/remote-config';
import { Overlay } from '@toro/common/shared/resources/Overlay';
import { TraderTrialInfoReducer } from './state-management/trader-trial/trader-trial.reducer';
import { TraderTrialInfoEffects } from './state-management/trader-trial/trader-trial.effects';
import { TraderClubReducer } from './state-management/club-trader/club-trader.reducer';
import { TraderClubEffects } from './state-management/club-trader/club-trader.effects';
import { SantanderSuitabilityService } from '@toro/common/shared/services/santander-suitability/santander-suitability-service';
import { SuitabilitySantanderReducer } from '@toro/common/shared/state-management/suitability-santander/suitability-santander.reducer';
import { OperationalLimitReducer } from '@toro/common/shared/state-management/operational-limit/operational-limit.reducer';

export function initializeAppSettingsAndFlags(
  settings: SettingsService,
  featureFlags: FeatureFlagsService,
) {
  // Encadeia o carregamento: `SettingsService.loadEnvironmentSettings()`
  // precisa completar ANTES de `FeatureFlagsService.initialize()`
  return () =>
    settings.loadEnvironmentSettings().then(() => featureFlags.initialize());
}

registerLocaleData(ptBr);

const routerOptions: ExtraOptions = {
  scrollPositionRestoration: 'top', // rolar a janela para o topo
  anchorScrolling: 'enabled', // permite rolar para âncoras
};
@NgModule({
  declarations: [
    AppComponent,
    CustomPlanComponent,
    TransferModalComponent,
    Overlay
  ],
  imports: [
    DraftModule,
    FooterModule,
    HubNavModule,
    RouterModule,
    AppcuesModule,
    ConfirmModule,
    BrowserModule,
    SvgIconModule,
    ChatBlipModule,
    MatInputModule,
    AgreementModule,
    MatSelectModule,
    MatDialogModule,
    HomeLoggedModule,
    TrSideMenuModule,
    HttpClientModule,
    CpfCnpjPipeModule,
    TrAccordionModule,
    SimpleModalModule,
    HeaderBalanceModule,
    DialogDepositModule,
    ChatOnboardingModule,
    ReactiveFormsModule,
    ZendeskHelpButtonModule,
    AngularFireRemoteConfigModule,
    TransferModule,
    MainTransferQuestionModule,
    TransferQuestionsModule,
    BrowserAnimationsModule,
    TrackingDirectiveModule,
    CustomWalletBuilderModule,
    RouterModule.forRoot(routes, routerOptions),
    UnauthorizedErrorHandlerModule,
    TrSpinnerButtonDirectiveModule,
    LiveSectionAdvisoryModule,
    CustomerWalletRoutesModule,
    MatProgressSpinnerModule,
    RewardsModule,
    StoreModule.forRoot({
      Files: FilesReducer,
      utils: UtilsReducer,
      banks: BanksReducer,
      finance: FinanceReducer,
      billing: BillingReducer,
      exchange: ExchangeReducer,
      research: ResearchReducer,
      liveStream: LiveStreamReducer,
      agreements: AgreementsReducer,
      salesforce: SalesForceReducer,
      userProfile: UserProfileReducer,
      environment: EnvironmentReducer,
      suitability: SuitabilityReducer,
      suitabilitySantander: SuitabilitySantanderReducer,
      fixedInvest: FixedInvestReducer,
      infoproducts: InfoproductsReducer,
      authorization: AuthorizationReducer,
      notifications: NotificationsReducer,
      authentication: UsersReducer,
      exchangeCheckout: StocksExchangeCheckoutReducer,
      digitalSubscription: DigitalSubscriptionReducer,
      institutionalAccounts: InstitutionalAccountsReducer,
      wealthEvolution: WealthEvolutionReducer,
      userAcceptCampaign: UserAcceptCampaignReducer,
      advisor: AdvisorReducer,
      customerWallet: CustomerWalletReducer,
      userTag: UserTagReducer,
      trader: TraderInfoReducer,
      traderTrial: TraderTrialInfoReducer,
      profileTraderClub: TraderClubReducer,
      advancedPlatforms: AdvancedPlatformsReducer,
      stvmPortabilities: STVMDigitalReducer,
      operationalLimit: OperationalLimitReducer,
      oneViewPermissions: OneViewPermissionsReducer,
    }),
    StoreModule.forFeature('stocksExchange', {
      wallet: StocksExchangeWalletReducer,
      stocksMarket: StocksMarketReducer
    }),
    EffectsModule.forRoot([
      FilesEffects,
      UsersEffects,
      BanksEffects,
      UtilsEffects,
      FinanceEffects,
      ResearchEffects,
      BillingEffects,
      TrackingEffects,
      ExchangeEffects,
      LiveStreamEffects,
      AgreementsEffects,
      SalesForceEffects,
      UserProfileEffects,
      SuitabilityEffects,
      SuitabilityEffects,
      EnvironmentEffects,
      InitializerEffects,
      FixedInvestEffects,
      InfoproductsEffects,
      StocksMarketEffects,
      NotificationsEffects,
      AuthorizationEffects,
      DigitalSubscriptionEffects,
      StocksExchangeWalletEffects,
      InstitutionalAccountsEffects,
      StocksExchangeCheckoutEffects,
      UserAcceptCampaignEffects,
      AdvisorEffects,
      CustomerWalletEffects,
      OperationalLimitEffects,
      UserTagEffects,
      TraderInfoEffects,
      TraderTrialInfoEffects,
      TraderClubEffects,
      AdvancedPlatformsEffects,
      STVMDigitalEffects,
      OneViewPermissionsEffects,
      SuitabilitySantanderEffects
    ]),
    EffectsModule.forFeature([
      StocksMarketEffects,
      StocksExchangeWalletEffects
    ]),
    environment.production
      ? []
      : StoreDevtoolsModule.instrument({ maxAge: 1120 })
  ],
  providers: [
    TextUtils,
    FormsUtils,
    HttpService,
    LayerService,
    BanksService,
    SentryService,
    DialogsService,
    FinanceService,
    BackendService,
    NetworkService,
    InternetService,
    ComputerService,
    SettingsService,
    RealtimeService,
    RealtimeQuoteService,
    TrackingService,
    DateFormatService,
    RouterTrackerService,
    // LoggedDomainGuard,
    AuthGuard,
    UserManagerService,
    UserProfileService,
    LoginWraperService,
    PushNotificationService,
    AuthorizationWraperService,
    BackendToFrontendParserService,
    SantanderSuitabilityService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppSettingsAndFlags,
      deps: [SettingsService, FeatureFlagsService],
      multi: true
    },
    {
      provide: FIREBASE_OPTIONS,
      useFactory: (service: SettingsService) =>
        service.getConfigurationKeyValue('firebase'),
      deps: [SettingsService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TrailInterceptor,
      multi: true
    },
    {
      provide: LOCALE_ID,
      useValue: 'pt'
    },
    environment.production
      ? {
          provide: ErrorHandler,
          useClass: CustomErrorHandler
        }
      : [],
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler
    },
    { provide: REMOTE_CONFIG_DEFAULTS, useValue: { enableAwesome: true } },
    {
      provide: REMOTE_CONFIG_SETTINGS,
      useFactory: () =>
        isDevMode()
          ? { minimumFetchIntervalMillis: 10_000, fetchTimeoutMillis: 60_000 }
          : { minimumFetchIntervalMillis: 60_000, fetchTimeoutMillis: 60_000 }
    }
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    TransferModalComponent,
    CheckingAccountReceiptComponent,
    DialogDepositComponent
  ]
})
export class AppModule {}
